import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Box, MenuItem, Select } from '@mui/material'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import { MrGroup } from '../../../features/curator/mentorReport'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import { filterField, filterLabel } from '../../../components/filterSelect/FilterSelect'
import { HeadFiltersState } from '../../head/headData'
import { capitalize } from '../../../utils/functions'
import { tone, Tone, ui } from '../../../utils/uiTokens'

export const page = {
    background: ui.card,
    border: `1px solid ${ui.border}`,
    borderRadius: '16px',
    padding: { xs: '16px', md: '24px' },
    minHeight: '80vh',
}

export const card = {
    background: ui.card,
    border: `1px solid ${ui.border}`,
    borderRadius: '16px',
    padding: { xs: '14px', md: '18px' },
    minWidth: 0,
}

export const inputSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': { borderRadius: ui.radius, backgroundColor: ui.card, fontSize: '14px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
    '& .MuiInputLabel-root.Mui-focused': { color: ui.pink },
}

export const chip = (t: Tone) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 10px',
    borderRadius: '24px',
    fontSize: '12.5px',
    fontWeight: 600,
    color: tone[t].text,
    backgroundColor: tone[t].bg,
    border: `1px solid ${tone[t].border}`,
    whiteSpace: 'nowrap' as const,
})

export const primaryButton = {
    backgroundColor: ui.pink,
    color: ui.card,
    borderRadius: ui.radius,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    '&:hover': { backgroundColor: ui.pinkDark, boxShadow: 'none' },
}

export const ghostButton = {
    color: ui.pink,
    borderColor: ui.border,
    borderRadius: ui.radius,
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': { borderColor: ui.pink, backgroundColor: ui.pinkSoft },
}

export const useGroupTitle = () => {
    const { locale } = useIntl()
    return (g: MrGroup) => {
        const subject = g[`subjectTitle${capitalize(locale)}` as 'subjectTitleKz'] || g.subjectTitleRu
        return [g.groupTitle, subject].filter(Boolean).join(' · ')
    }
}

export const mentorNames = (g: MrGroup) => g.mentors.map((m) => m.name || `#${m.id}`).join(', ')

// Фильтры ментора: лек, топ, ай/апта. Ключи общие с рейтингом, выбор сохраняется между страницами.
// Отчёт без лека пустой: берём первый лек, тогда MonthWeekFilter сам ставит текущие ай/апта
export const useDefaultCourse = (f: HeadFiltersState) => {
    const { data } = curatorStudentsApiSlice.useFetchCuratorCoursesQuery()
    const first = data?.data?.[0]?.id
    useEffect(() => {
        if (!f.courseId && first) f.setCourseId(first)
    }, [f.courseId, first])
}

export const MentorFilters: React.FC<{ f: HeadFiltersState }> = ({ f }) => {
    const { formatMessage, locale } = useIntl()
    const { data: courseResponse } = curatorStudentsApiSlice.useFetchCuratorCoursesQuery()
    const courses = courseResponse?.data ?? []
    useDefaultCourse(f)
    const { data: groups = [] } = curatorStudentsApiSlice.useFetchCuratorGroupsQuery(f.courseId ?? 0, { skip: !f.courseId })

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', marginBottom: '20px' }}>
            <Box sx={{ minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                <Box sx={filterLabel}>{formatMessage({ id: 'headLek' })}</Box>
                <Select
                    size="small"
                    displayEmpty
                    value={f.courseId ?? ''}
                    onChange={(e) => {
                        f.setCourseId(Number(e.target.value) || null)
                        f.setGroupId(null)
                    }}
                    sx={{ ...filterField, minWidth: { sm: 220 } }}
                >
                    <MenuItem value="">{formatMessage({ id: 'headAllLeks' })}</MenuItem>
                    {courses.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c[`title${capitalize(locale)}`] || c.titleRu}</MenuItem>
                    ))}
                </Select>
            </Box>
            <Box sx={{ minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                <Box sx={filterLabel}>{formatMessage({ id: 'groups' })}</Box>
                <Select
                    size="small"
                    displayEmpty
                    disabled={!f.courseId}
                    value={f.groupId ?? ''}
                    onChange={(e) => f.setGroupId(Number(e.target.value) || null)}
                    sx={{ ...filterField, minWidth: { sm: 220 } }}
                >
                    <MenuItem value="">{formatMessage({ id: 'allGroups' })}</MenuItem>
                    {groups.map((g) => (
                        <MenuItem key={g.id} value={g.id}>{g.title}</MenuItem>
                    ))}
                </Select>
            </Box>
            <MonthWeekFilter
                courseId={f.courseId}
                monthNumber={f.monthNumber}
                weekNumber={f.weekNumber}
                onChange={(m, w) => {
                    f.setMonthNumber(m)
                    f.setWeekNumber(w)
                }}
            />
        </Box>
    )
}

export const Hint: React.FC<{ id: string }> = ({ id }) => {
    const { formatMessage } = useIntl()
    return <Box sx={{ color: ui.muted, padding: '24px 0', fontSize: '14px' }}>{formatMessage({ id })}</Box>
}
